import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ErrorBoundary from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";

// Landing ships in the main bundle since it is the entry point; everything else
// is split so a first visit doesn't download the whole app.
const ReportPage = lazy(() => import("@/pages/ReportPage"));
const Feed = lazy(() => import("@/pages/Feed"));
const Outlets = lazy(() => import("@/pages/Outlets"));
const OutletProfile = lazy(() => import("@/pages/OutletProfile"));
const Compare = lazy(() => import("@/pages/Compare"));
const History = lazy(() => import("@/pages/History"));
const Methodology = lazy(() => import("@/pages/Methodology"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const SignIn = lazy(() => import("@/pages/SignIn"));
const SignUp = lazy(() => import("@/pages/SignUp"));
const Account = lazy(() => import("@/pages/Account"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Legal = lazy(() => import("@/pages/Legal"));
const NotFound = lazy(() => import("@/pages/NotFound"));

/**
 * Start every navigation at the top of the page.
 *
 * The browser keeps the scroll offset across a client-side route change, so
 * following a link from halfway down a long report lands you halfway down the
 * next page — which reads as content missing rather than as a scroll position.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Navbar />
          <ErrorBoundary>
            <Suspense fallback={<div className="min-h-[60vh]" />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/report/:id" element={<ReportPage />} />
                <Route path="/feed" element={<Feed />} />
                <Route path="/outlets" element={<Outlets />} />
                {/* The domain is one path segment, so a host with a dot in it
                    matches but a slash never does — anything the API could not
                    place lands on the profile's own not-found state rather than
                    on a route that does not exist. */}
                <Route path="/outlets/:domain" element={<OutletProfile />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/history" element={<History />} />
                <Route path="/methodology" element={<Methodology />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/account" element={<Account />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* One document, three addresses. Payment processors and app
                    stores ask for /terms and /privacy by name, and splitting a
                    page this short into two files to satisfy a form field would
                    leave two documents to keep in step. */}
                <Route path="/legal" element={<Legal />} />
                <Route path="/terms" element={<Legal />} />
                <Route path="/privacy" element={<Legal />} />
                {/* Last, and matching everything the routes above did not. Without
                    it the router matched nothing, rendered nothing, and left the
                    previous route's title in the tab — which reads as a page that
                    failed to load rather than as an address that does not exist. */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
          <Footer />
          {/* Sonner's mobile offset is 16px from the top, which on a phone puts
              a top-centre toast squarely over the sticky header — the logo and
              the menu button both disappear under it for the life of the toast.
              The header is 42px of controls inside 12px of padding, so 78px
              clears it with a little air. Desktop keeps sonner's own offset. */}
          <Toaster position="top-center" mobileOffset={{ top: "78px" }} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
